import React, { useState, useEffect, useContext } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useManualQuery, ClientContext } from "graphql-hooks";

import {
  Container,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
  Divider,
  TextField,
  Radio,
  Checkbox,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  makeStyles,
  Link,
} from "@material-ui/core";
import Box from "@mui/material/Box";

import styled from "styled-components";
import Diversity1Icon from "@mui/icons-material/Diversity1";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

import { REFRESH_TOKEN_MUTATION } from "../../graphQL/user_qry";
import { GET_TEAM_ALL_TERMS, EDIT_TEAM_TERM_NOTES } from "../../graphQL/team_term_qry";

import Header from "./Header.js";
import EditTeamTermNotesDialog from "./EditTeamTermNotesDialog";
import { project_url } from "../../constants/Data";

const StyledTooltip = styled(props => (
  <Tooltip
    classes={{ popper: props.className }}
    {...props}
    PopperProps={{
      popperOptions: {
        modifiers: {
          flip: { enabled: false },
          offset: {
            offset: "0px, -50px, 20px, -25px",
          },
        },
      },
    }}
  />
))`
  & .MuiTooltip-tooltip {
    display: flex;
    background: #757575;
    color: #fff;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
  }
`;

const useStyles = makeStyles(theme => ({
  root: {
    marginRight: "8rem",
    textAlign: "center",
    padding: theme.spacing(2),
    paddingLeft: 0,
    paddingRight: 0,
    width: "1400px",
    paddingTop: "7rem",
    //  minWidth: "500px",
  },
  container: {
    padding: theme.spacing(4),
    marginTop: "1rem",
    marginBottom: "5rem",
    overflow: "hidden",
    clear: "both",
    borderRadius: "8px",
    height: "870px",
    backgroundColor: "#ffffff",
  },
  ComponentDragBox: {
    float: "left",
  },
  FormDropBox: {
    float: "left",
    marginLeft: "5rem",
    marginTop: ".5rem",
  },
  outlinedButtonStyle: {
    borderRadius: 8,
    width: "110px",
    padding: 0,
    height: "48px",
    textTransform: "none",
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    marginTop: ".7rem",
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    // "&:active": {
    //   backgroundColor: "#765285",
    // },
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  dataGridStyle: {
    "&.MuiDataGrid-scrollbar MuiDataGrid-scrollbar--horizontal css-1rtad1": {
      position: "relative",
      display: "block",
    },
    "&.MuiDataGrid-root": {
      //  minWidth: 980,
      //  width:"100%"
      //  overflowX: 'scroll'
    },

    "& .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      fontSize: 12,
    },
  },

  notesStyle: {
    cursor: "pointer",
    fontWeight: 500,
    borderRadius: "8px",
    height: 40,
    fontSize: 14,
    display: "flex",
    padding: 5,
    // paddingRight: 10,
    alignItems: "center",
    textAlign: "left",
    lineHeight: 1.2,
    // wordBreak: "break-word",
    minWidth: 150,
    "&:hover": {
      backgroundColor: "#E2F2FF",
    },
  },
  linkStyle: {
    height: 35,
    display: "flex",
    paddingLeft: 5,
    paddingRight: 5,
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#E2F2FF",
    },
  },
}));

function TeamTermNotes() {
  const classes = useStyles();
  const navigate = useNavigate();
  const client = useContext(ClientContext);
  const { teamUuid } = useParams();
  const [pathname] = useState(window.location.pathname);
  const [rows, setRows] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [loadContent, setLoadContent] = useState(false);
  const [openEditTeamTermNotesDialog, setOpenEditTeamTermNotesDialog] = useState(false);
  const [currentTeamTermData, setCurrentTeamTermData] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [reload, setReload] = useState(false);

  let clientName = "";
  if (localStorage.getItem("clientName")) clientName = localStorage.getItem("clientName");
  const [refreshToken] = useMutation(REFRESH_TOKEN_MUTATION, {
    onError: err => {
      console.log(err);
    },
  });
  useEffect(() => {
    if (localStorage.getItem("refreshToken")) {
      onRefreshToken();
    } else window.location.href = `${project_url}/signin/?next=${pathname}`;
  }, []);
  const onRefreshToken = async () => {
    const { data: refreshData, error: refreshError } = await refreshToken({
      variables: { refreshToken: localStorage.getItem("refreshToken") },
    });
    if (refreshError) {
      if (
        refreshError.graphQLErrors[0].message ===
        "You do not have permission to perform this action"
      )
        window.location.href = `${project_url}/signin/?next=${pathname}`;
      if (refreshError.graphQLErrors[0].message === "Signature has expired")
        window.location.href = `${project_url}/signin/?next=${pathname}`;
    }
    if (refreshData && refreshData.refreshToken) {
      localStorage.setItem("token", refreshData.refreshToken.token);
      localStorage.setItem("refreshToken", refreshData.refreshToken.refreshToken);
      client.setHeader("Authorization", `JWT ${refreshData.refreshToken.token}`);
      setLoadContent(true);
    }
  };

  let clientId = 1;
  if (localStorage.getItem("clientId")) clientId = localStorage.getItem("clientId");

  const formatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const dayFormat = day => {
    let newFormat = "-";
    if (day) {
      const dayDate = new Date(day);
      const dayFormat = formatter.formatToParts(dayDate);
      newFormat =
        dayFormat.find(a => a.type == "month").value +
        "/" +
        dayFormat.find(a => a.type == "day").value +
        "/" +
        dayFormat.find(a => a.type == "year").value;
    }
    return newFormat;
  };

  const [getTeamAllTermsListQuery] = useManualQuery(GET_TEAM_ALL_TERMS);

  const getTeamAllTermsList = async () => {
    const {
      loading: termsListLoading,
      error: termsListError,
      data: termsListData,
    } = await getTeamAllTermsListQuery({
      variables: {
        teamUuid: teamUuid,
      },
    });
    if (!termsListLoading && termsListData && termsListData.getTeamAllTerms) {
      setRows(
        termsListData.getTeamAllTerms.map(teamTerm => ({
          id: teamTerm.teamTermId,
          termUuid: teamTerm.termId.termUuid,
          // termName: teamTerm.termId.name,
          sprintName: teamTerm.termId.name,
          startDate: teamTerm.termId.startDate
            ? dayFormat(teamTerm.termId.startDate)
            : "",
          endDate: teamTerm.termId.endDate ? dayFormat(teamTerm.termId.endDate) : "",
          teamName: teamTerm.teamId.name,
          notes: teamTerm.note,
          noOfResources: 123,
        }))
      );
      termsListData &&
        termsListData.getTeamAllTerms.length &&
        setTeamName(termsListData.getTeamAllTerms[0].teamId.name);
    }
    if (termsListError) {
      // if (termsListError.graphQLErrors[0].message === "Signature has expired")
      navigate(`${project_url}/signin`);
    }
  };

  useEffect(() => {
    if (firstLoad) {
      window.scrollTo(0, 0);
      setFirstLoad(false);
    }
    (() => getTeamAllTermsList())();
  }, [reload]);

  const columns = [
    {
      field: "sprintName",
      headerName: "Sprint Name",
      minWidth: 130,
      flex: 1,
      // sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
    },
    {
      field: "startDate",
      headerName: "Start Date",
      minWidth: 100,
      flex: 1,
      // sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
    },
    {
      field: "endDate",
      headerName: "End Date",
      minWidth: 100,
      flex: 1,
      // sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
    },
    {
      field: "noOfResources",
      headerName: "No of Resources",
      minWidth: 100,
      flex: 1,
      // sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
    },
    {
      field: "notes",
      headerName: "Notes",
      minWidth: 650,
      flex: 1,
      // sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,

      renderCell: rows => {
        console.log(rows.row.notes.length);
        let notesText =
          rows.row.notes.length > 100
            ? rows.row.notes.slice(0, 100) + "..."
            : rows.row.notes;

        return rows.row.notes.trim() !== "" ? (
          <StyledTooltip title="Update Notes">
            <Typography
              variant="h3"
              className={classes.notesStyle}
              onClick={() => {
                setCurrentTeamTermData(rows.row);
                setOpenEditTeamTermNotesDialog(true);
              }}
            >
              {notesText}
            </Typography>
          </StyledTooltip>
        ) : (
          <Link>
            <Typography
              variant="h3"
              className={classes.notesStyle}
              onClick={() => {
                setCurrentTeamTermData(rows.row);
                setOpenEditTeamTermNotesDialog(true);
              }}
            >
              Update Notes
            </Typography>
          </Link>
        );
      },
    },
  ];

  const [editTeamTermNotes] = useMutation(EDIT_TEAM_TERM_NOTES);
  const handleDone = async newNotesVal => {
    if (currentTeamTermData.notes === newNotesVal) return;

    const { loading, data, error } = await editTeamTermNotes({
      variables: {
        teamTermId: parseInt(currentTeamTermData.id),
        notes: newNotesVal,
      },
    });
    if (error) {
      alert("Unfortunately an error occurred.");
    } else if (data && data.editTeamTermNotes) {
      setReload(!reload);
    }
  };

  function DataGridTitle() {
    return (
      <>
        <Grid
          container
          xs={12}
          style={{
            paddingTop: 20,
            paddingBottom: 15,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid item xs={10} style={{ textAlign: "center" }}>
            <Typography variant="h1" style={{ fontSize: 20, paddingLeft: 100 }}>
              Notes for team: {teamName}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: 10,
            }}
          ></Grid>
        </Grid>
        <Divider />
      </>
    );
  }
  if (loadContent)
    return (
      <div className={classes.root}>
        <Header
          breadcrumbs={[
            { label: teamName, link: null },
            { label: "Notes", link: null },
          ]}
        />
        <div className={classes.container}>
          <DataGrid
            className={classes.dataGridStyle}
            columns={columns}
            rows={rows}
            showCellRightBorder={true}
            // hideFooter={true}
            slots={{
              toolbar: DataGridTitle,
            }}
            getRowId={row => row.id}
            autoHeight={true}
            experimentalFeatures={{ newEditingApi: true }}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              // sorting: {
              //   sortModel: [{ field: "endDate", sort: "desc" }],
              // },
            }}
            pageSizeOptions={[10, 25, 50]}
          />
          {openEditTeamTermNotesDialog && (
            <EditTeamTermNotesDialog
              teamName={currentTeamTermData.teamName}
              sprintName={currentTeamTermData.sprintName}
              notes={currentTeamTermData.notes}
              closeDialog={() => {
                setOpenEditTeamTermNotesDialog(false);
              }}
              handleDone={newNotesVal => handleDone(newNotesVal)}
            />
          )}
        </div>
      </div>
    );
  else return;
}

export default TeamTermNotes;
